// src/contexts/AuthContext.tsx
import { NotificationProvider } from '@/components/notifications/context/NotificationContext';
import { UserType } from '@/features/auth/interfaces';
import { authApi } from 'api/auth';
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: UserType | null;
  loading: boolean;
  verify2FA: (token: string, rememberMe: boolean, captchaToken: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  changeAvatar: (file: File) => Promise<void>;
  deleteAvatar: () => Promise<void>;
  getUserInitials: () => string;
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(localStorage.getItem('accessToken'));

  useEffect(() => {
    checkAuth();
  }, []);

  const logout = useCallback(async () => {
    console.log('Logging out...');
    await authApi.logout().finally(() => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tempToken');
      localStorage.removeItem('sessionId');
      setUser(null);
      setToken(null);
      navigate('/login');
    });
  }, [navigate]);

  const checkAuth = useCallback(async () => {
    // Pe pagina de impersonare fluxul de autentificare e gestionat integral de
    // componenta Impersonate (schimbă tokenul din ERP, apoi navighează în /app).
    // Dacă am rula aici me()/logout(), un logout() asincron ar șterge tokenul
    // abia obținut și ar redirecta spre /login (race condition).
    if (window.location.pathname.startsWith('/impersonate')) {
      console.log('[auth] checkAuth: pagină de impersonare, las fluxul pe seama componentei Impersonate');
      setLoading(false);
      return;
    }

    // Fără token în localStorage nu are sens un me() care oricum dă 401 și ar
    // declanșa un logout() distructiv (șterge token-uri + navigate('/login')).
    // Redirectarea rutelor protejate e responsabilitatea AuthMiddleware.
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      console.log('[auth] checkAuth: nu există accessToken, rămân neautentificat fără logout');
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const { data } = await authApi.me();
      console.log('[auth] checkAuth: utilizator autentificat', data?.email ?? data?.id);
      setUser(data);
    } catch (error) {
      console.log('[auth] checkAuth: me() a eșuat, fac logout', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  const refreshAuth = async () => {
    checkAuth();
  }

  const verify2FA = async (token: string, rememberMe: boolean, captchaToken: string) => {
    const { data } = await authApi.verify2FA({ token, rememberMe, captchaToken });
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    if (data.sessionId) localStorage.setItem('sessionId', data.sessionId);
    setToken(data.accessToken);
    setUser(data.user);
  };

  const changeAvatar = async (file: File) => {
    const response = await authApi.changeAvatar(file);
    // Update the user state with the new avatar URLs
    if (response.data && user) {
      const updatedUser = { 
        ...user, 
        avatar: response.data.avatarUrl,
        optimizedAvatar: response.data.optimizedAvatarUrl
      };
      setUser(updatedUser);
    }
  };

  const deleteAvatar = async () => {
    await authApi.deleteAvatar();
    // Update the user state to remove avatar URLs
    if (user) {
      const updatedUser = { 
        ...user, 
        avatar: null,
        optimizedAvatar: null
      };
      setUser(updatedUser);
    }
  };

  const getUserInitials = useCallback(() => {
    if (!user) return 'U';
    const names = user.fullName?.split(' ') || [];
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return user.fullName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U';
  }, [user]);

  // Define value object
  const value: AuthContextType = {
    user,
    loading,
    verify2FA,
    logout,
    refreshAuth,
    changeAvatar,
    deleteAvatar,
    getUserInitials,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* <NotificationProvider authToken={token || undefined}> */}
        {!loading && children}
      {/* </NotificationProvider> */}
    </AuthContext.Provider>
  );
};